import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Separator } from "../../components/ui/separator";
import {
  User,
  Key,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import logger from "../../utils/logger";

const Profile = () => {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  const [loading, setLoading] = useState({
    profile: false,
    password: false,
  });
  
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("superAdminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/profile`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setProfileData({
        name: response.data.data.name || response.data.data.full_name,
        email: response.data.data.email,
      });
    } catch (error) {
      logger.error("Error fetching profile:", error);
      showAlert("error", "فشل في تحميل بيانات الملف الشخصي");
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, profile: true }));
    setAlert(null);
    
    try {
      const token = localStorage.getItem("superAdminToken");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/profile`,
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      showAlert("success", "تم تحديث بيانات الملف الشخصي بنجاح");
    } catch (error) {
      logger.error("Error updating profile:", error);
      showAlert("error", error.response?.data?.message || "فشل في تحديث الملف الشخصي");
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.new_password !== passwordData.confirm_password) {
      showAlert("error", "كلمات المرور الجديدة غير متطابقة");
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      showAlert("error", "كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل");
      return;
    }
    
    setLoading(prev => ({ ...prev, password: true }));
    setAlert(null);
    
    try {
      const token = localStorage.getItem("superAdminToken");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/change-password`,
        passwordData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      
      showAlert("success", "تم تغيير كلمة المرور بنجاح");
    } catch (error) {
      logger.error("Error changing password:", error);
      showAlert("error", error.response?.data?.message || "فشل في تغيير كلمة المرور");
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">الملف الشخصي</h1>
        <p className="text-slate-600 mt-1">
          إدارة معلومات حسابك وكلمة المرور
        </p>
      </div>

      {alert && (
        <Alert variant={alert.type === "success" ? "default" : "destructive"}>
          {alert.type === "success" ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              معلومات الملف الشخصي
            </CardTitle>
            <CardDescription>
              تحديث معلومات حسابك الشخصي
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">الاسم الكامل</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="أدخل اسمك الكامل"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="أدخل بريدك الإلكتروني"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={loading.profile}
                className="flex items-center gap-2"
              >
                {loading.profile ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
                    جاري التحديث...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    حفظ التغييرات
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              تغيير كلمة المرور
            </CardTitle>
            <CardDescription>
              تأكد من استخدام كلمة مرور قوية
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">كلمة المرور الحالية</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="أدخل كلمة المرور الحالية"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2"
                    onClick={() => togglePasswordVisibility("current")}
                  >
                    {showPasswords.current ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">كلمة المرور الجديدة</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="أدخل كلمة المرور الجديدة"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2"
                    onClick={() => togglePasswordVisibility("new")}
                  >
                    {showPasswords.new ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">تأكيد كلمة المرور</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder="أدخل كلمة المرور الجديدة مرة أخرى"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2"
                    onClick={() => togglePasswordVisibility("confirm")}
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <Button 
                type="submit" 
                disabled={loading.password}
                className="flex items-center gap-2"
              >
                {loading.password ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
                    جاري تغيير كلمة المرور...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4" />
                    تغيير كلمة المرور
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Security Tips */}
      <Card>
        <CardHeader>
          <CardTitle>نصائح الأمان</CardTitle>
          <CardDescription>
            كيفية الحفاظ على أمان حسابك
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-slate-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>استخدم كلمة مرور قوية تحتوي على أحرف وأرقام ورموز خاصة</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>لا تشارك كلمة المرور مع أي شخص</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>قم بتغيير كلمة المرور بشكل دوري</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>تأكد من تسجيل الخروج عند الانتهاء من استخدام النظام</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;